import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { modalConfig } from './modal-config';

/**
 * shadcn-style Dialog on @radix-ui/react-dialog (F5-1).
 *
 * Radix owns the accessibility: focus moves in and is trapped, focus
 * returns on close, ESC and outside-press go to the top layer only
 * (one layer stack shared with the StyledSelect / DatePicker popovers),
 * the page scroll lock is counted, and siblings get `aria-hidden`.
 *
 * `Modal.tsx` is built on these parts and keeps its props API; new
 * compositional code (Phase 2) uses `DialogContent` directly. Z-index
 * is single-sourced from `modalConfig.zIndex` (overlay 9998, box 9999);
 * popovers sit above at z-[10000].
 */

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, style, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 bg-black/50', className)}
    style={{ zIndex: modalConfig.zIndex.overlay, ...style }}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, style, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      aria-modal="true"
      className={cn(
        'fixed left-1/2 top-1/2 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 rounded-xl border bg-white p-6 shadow-lg focus:outline-none',
        className,
      )}
      style={{ zIndex: modalConfig.zIndex.modal, ...style }}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm text-gray-500 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:pointer-events-none">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:gap-2', className)} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none text-gray-900', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn('text-sm text-gray-600', className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

/**
 * The unstyled Radix content part, for primitives (like `ListingsDialog`)
 * that nest the box inside the overlay instead of using `DialogContent`'s
 * fixed-centre layout. `DialogShell` below does the same with the focus
 * return and dismissal flags built in.
 */
const DialogContentPrimitive = DialogPrimitive.Content;

/** The unstyled Radix description part, for primitives (like `ConfirmModal`)
 *  whose message keeps its own typography. */
const DialogDescriptionPrimitive = DialogPrimitive.Description;

/** The unstyled Radix title part, for a migrated overlay whose heading keeps
 *  its own classes (`<DialogTitlePrimitive asChild><h3 …>`): the styled
 *  `DialogTitle` would add `leading-none` to it. */
const DialogTitlePrimitive = DialogPrimitive.Title;

/**
 * An Escape Radix let through. A dialog opened over another layer (a
 * confirm over a side panel, the listings lifecycle dialog over Agreement
 * details) ignores an Escape pressed just after it opens: its
 * DismissableLayer has joined the layer stack but has not re-rendered, so
 * neither it nor the layer below counts as the top one. Radix marks an
 * Escape it handled (or was told to keep) with preventDefault, on document
 * capture, before the box's own keydown; an unmarked one pressed inside
 * the box, with no popover open (a select or date picker in the same
 * moment), is the box's. The caller closes and calls preventDefault, so
 * the layers below and window listeners (DetailsSidePanel) skip it.
 */
function escapeDroppedByRadix(e: React.KeyboardEvent<HTMLElement>): boolean {
  return (
    e.key === 'Escape' &&
    !e.defaultPrevented &&
    e.currentTarget.contains(e.target as Node) &&
    !document.querySelector('[data-radix-popper-content-wrapper]')
  );
}

/** An open in-box combobox (an autocomplete whose list renders in the box, not in a Radix popover). */
const isOpenCombobox = (target: EventTarget | null) =>
  target instanceof HTMLElement && target.matches('[role="combobox"][aria-expanded="true"]');

export interface DialogShellProps {
  open: boolean;
  /** Every dismissal (ESC, backdrop press, a close button) ends here. */
  onClose: () => void;
  closeOnEscape?: boolean;
  /** A press that starts on the backdrop itself closes. */
  closeOnOverlayClick?: boolean;
  /** The backdrop's layout (e.g. `flex items-start justify-center pt-24`). */
  overlayClassName?: string;
  overlayStyle?: React.CSSProperties;
  /** The box's own classes, kept exactly as given. */
  className?: string;
  style?: React.CSSProperties;
  role?: 'dialog' | 'alertdialog';
  /** Element to focus on open instead of the first tabbable one */
  initialFocusRef?: React.RefObject<HTMLElement>;
  /** Set when `children` render a `DialogDescription`; it becomes the dialog's aria-describedby */
  hasDescription?: boolean;
  children: React.ReactNode;
}

/**
 * The shared modal shell (F5-3): Radix Root + Portal + Overlay + Content,
 * with the box nested inside the backdrop so each overlay keeps its own
 * Tailwind box and alignment. `Modal` and every migrated hand-rolled
 * overlay are built on it. Callers label it with their existing heading
 * in `<DialogTitlePrimitive asChild>` and a subtitle in
 * `<DialogDescriptionPrimitive asChild>` (with `hasDescription`), which
 * keep the heading's own classes.
 */
function DialogShell({
  open,
  onClose,
  closeOnEscape = true,
  closeOnOverlayClick = true,
  overlayClassName,
  overlayStyle,
  className,
  style,
  role,
  initialFocusRef,
  hasDescription = false,
  children,
}: DialogShellProps) {
  const overlayRef = React.useRef<HTMLDivElement>(null);
  // The shell is opened by consumer state, not a Radix <Trigger>, so Radix
  // has no trigger to refocus on close. Remember what had focus when the
  // dialog opened and put it back. Captured while rendering the open
  // transition: by the time any effect runs, an `autoFocus` field inside
  // the dialog has already taken focus (and Radix then skips its
  // open-autofocus event entirely).
  const returnFocusRef = React.useRef<HTMLElement | null>(null);
  const wasOpenRef = React.useRef(false);
  if (open !== wasOpenRef.current) {
    wasOpenRef.current = open;
    if (open) {
      returnFocusRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogPortal>
        <DialogOverlay ref={overlayRef} className={overlayClassName} style={overlayStyle}>
          <DialogPrimitive.Content
            // Radix spreads content props over its own role="dialog", so
            // an explicit undefined would erase it: pass only when set.
            {...(role ? { role } : {})}
            aria-modal="true"
            // Without a DialogDescription in children, opt out of Radix's
            // warning; with one, Radix links it.
            {...(hasDescription ? {} : { 'aria-describedby': undefined })}
            className={className}
            style={{
              ...style,
              zIndex: modalConfig.zIndex.modal,
              outline: 'none',
            }}
            // Clicks inside the box don't reach React ancestors of the
            // dialog (it renders in a portal, but React events bubble
            // through the component tree).
            onClick={(e) => e.stopPropagation()}
            onOpenAutoFocus={(event) => {
              const el = initialFocusRef?.current;
              if (!el) return;
              el.focus();
              // A disabled target can't take focus: leave Radix's default.
              if (document.activeElement === el) event.preventDefault();
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              const el = returnFocusRef.current;
              if (el?.isConnected) el.focus();
            }}
            onEscapeKeyDown={(event) => {
              if (!closeOnEscape) event.preventDefault();
              // ESC in an open in-dialog combobox (an autocomplete whose
              // list renders in the box, not in a Radix popover) closes
              // that list only: it is the top layer.
              if (isOpenCombobox(event.target)) event.preventDefault();
            }}
            onKeyDown={(e) => {
              // The same rules for an Escape Radix dropped just after opening.
              if (closeOnEscape && !isOpenCombobox(e.target) && escapeDroppedByRadix(e)) {
                e.preventDefault();
                onClose();
              }
            }}
            onPointerDownOutside={(event) => {
              // Only a press on the backdrop itself dismisses; presses on
              // other page layers (toasts etc.) never do.
              const target = event.detail.originalEvent.target;
              if (!closeOnOverlayClick || target !== overlayRef.current) {
                event.preventDefault();
              }
            }}
          >
            {children}
          </DialogPrimitive.Content>
        </DialogOverlay>
      </DialogPortal>
    </Dialog>
  );
}

/**
 * The backdrop and box of the search pickers built on DialogShell
 * (AddSpaceDialog, ParentPropertyPickerModal, ReassignAllocationProperty-
 * Modal, AddDealBrokerModal). Safe at any height (Matt 2026-10-22, item 25):
 * the top offset shrinks on a short viewport, the box never grows past the
 * viewport, and if it still doesn't fit the backdrop scrolls.
 */
const PICKER_OVERLAY =
  'flex items-start justify-center overflow-y-auto bg-black/40 pt-[min(6rem,10vh)] pb-4';
const PICKER_BOX =
  'bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 flex flex-col max-h-[min(80vh,calc(100dvh_-_min(6rem,10vh)_-_1rem))]';

export {
  PICKER_OVERLAY,
  PICKER_BOX,
  DialogShell,
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogContentPrimitive,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogDescriptionPrimitive,
  DialogTitlePrimitive,
  escapeDroppedByRadix,
};
